import * as React from 'react';
import { HostAppColors } from '../../styles/HostAppColors';
import { ContainerErrorType, ContainerWarning, ICriticalContainerError } from '@fluidframework/container-definitions';
import { DriverErrorType } from '@fluidframework/driver-definitions';
import { OdspErrorType } from '@fluidframework/odsp-driver-definitions';
import { LoadedInfo, OWHErrorType, OfficeFluidErrorType, CancellationError } from '@fluidx/office-fluid-types';
import { Locale, LocaleDirection, useStringTable } from '@fluidx/utilities';
import { getStringTable } from '../../../utilities/localizationUtilities';
import { DialogErrorInfo, getDialogMessagingForError } from './getDialogMessagingForError';
import { ErrorDialog, ErrorDialogActionButton } from './ErrorDialog';

export interface ContainerErrorDialogProps {
  /**
   * Promise to the LoadedInfo. The LoadedInfo object has reference to two promises,
   * a Promise to the component that's getting loaded, and a promise to the container.
   */
  loadedInfoPromise: Promise<LoadedInfo>;

  /**
   * Theme colors of the Hosting App
   */
  appColors: HostAppColors;

  /** Host localization */
  localization: {
    /** Host locale */
    locale: Locale;

    /** Host UI direction. If not provided, this will be determined from Host locale */
    localeDirection?: LocaleDirection;
  };

  /**
   * Action handlers for different error scenarios.
   * It's implementation should fan out to handle all expected action types T.
   * If callback is not provided, the error dialog simply dismisses when action buttons are clicked
   */
  errorDialogActionHandlers?: <ActionType extends 'Close' | 'Feedback', ErrorType extends OfficeFluidErrorType>(
    actionType: ActionType,
    errorType: ErrorType
  ) => void;
}

/**
 * A UX control to handle OnLoad and PostLoad fluid errors using Dialog messaging
 * OnLoad errors handled could originate from OfficeWebHost (OWHErrorType) or Fluid Driver (DriverErrorType)
 * PostLoad errors handled originate from Fluid Container and can be of ContainerErrorType or DriverErrorType.
 *
 * Where this control is too opinionated on things like recovery actions possible for each error handled and easy configuration is not possible,
 * It's recommended a host builds their own equivalent error handling control using with the underlying error handling utilities
 */
export const ContainerErrorDialog = (props: ContainerErrorDialogProps) => {
  const { errorDialogActionHandlers, loadedInfoPromise, appColors, localization } = props;

  const [errorInfo, setErrorInfo] = React.useState<DialogErrorInfo & { error: unknown }>();
  const [serviceBusyOnLoadPath, setServiceBusyOnLoadPath] = React.useState<boolean>(false);

  // This is only needed for additional strings like Reload action button text
  // Ideally, these type of string should come from host string table
  const stringTable = useStringTable(getStringTable, localization.locale);

  const handleLoadError = React.useCallback(async (error: unknown) => {
    // If the error is an intentional cancellation, then just ignore it.
    if (error instanceof CancellationError) {
      return;
    }

    const onLoadErrorInfo = await getDialogMessagingForError(error, localization.locale);

    if (onLoadErrorInfo?.errorType === DriverErrorType.throttlingError) {
      setServiceBusyOnLoadPath(true);
    }

    setErrorInfo({ ...onLoadErrorInfo, error });
  }, []);

  const handlePostLoadError = React.useCallback(async (error: ICriticalContainerError) => {
    const postLoadErrorInfo = await getDialogMessagingForError(error, localization.locale);

    setErrorInfo({ ...postLoadErrorInfo, error });
  }, []);

  // TODO: Task 4623211: Define messaging experience for Container warnings
  const handlePostLoadWarning = React.useCallback(async (_warning: ContainerWarning) => {
    // We currently don't show dialogs on post-load container warnings
  }, []);

  React.useEffect(() => {
    const containerClosedHandler = (error?: ICriticalContainerError) => {
      /**
       * Container may close for a variety of reasons, including the fact that a host may choose to close it
       * Only react to container close event with an error dialog if there's an error associated with the event
       */
      error && handlePostLoadError(error).catch(() => {});
    };

    const containerWarningHandler = (warning: ContainerWarning) => {
      handlePostLoadWarning(warning).catch(() => {});
    };

    loadedInfoPromise
      .then((loadedInfo) => {
        // Handling container and component promise rejections separately because office web host rejects them individually
        loadedInfo.container
          .then((container) => {
            container.on('closed', containerClosedHandler);
            container.on('warning', containerWarningHandler);
          })
          .catch((error) => {
            handleLoadError(error).catch(() => {});
          });
        loadedInfo.component.catch((error) => {
          handleLoadError(error).catch(() => {});
        });
      })
      .catch((error) => {
        // There are plans for office web host to move to single promise rejection model.
        // When that happens instead of rejecting individual container/component promise
        // office web host will reject the promise to loadedInfo. Keeping the catch block as a safety net for that change.
        handleLoadError(error).catch(() => {});
      });

    return () => {
      loadedInfoPromise
        .then((loadedInfo) => {
          loadedInfo.container
            .then((container) => {
              container.removeListener('closed', containerClosedHandler);
              container.removeListener('warning', containerWarningHandler);
            })
            // we don't want rejections from here to bubble to the window object
            // since these are handled by the handleLoadFailure method when the promise
            // to loadedInfo resolves after the ContainerErrorDialog mounts.
            .catch(() => {});
        })
        .catch(() => {});

      setErrorInfo(undefined);
    };
  }, [loadedInfoPromise]);

  // Action Handlers
  const feedbackAction: ErrorDialogActionButton = {
    type: 'Primary',
    label: stringTable?.giveFeedback || '',
    action: () => {
      errorInfo && errorDialogActionHandlers && errorDialogActionHandlers('Feedback', errorInfo.errorType);
    }
  };

  const closeAction: ErrorDialogActionButton = {
    type: 'Default',
    label: stringTable?.close || '',
    action: () => {
      errorInfo && errorDialogActionHandlers && errorDialogActionHandlers('Close', errorInfo.errorType);
    }
  };

  if (errorInfo) {
    switch (errorInfo.errorType) {
      case DriverErrorType.fileNotFoundOrAccessDeniedError:
        return (
          <ErrorDialog
            dialogStrings={errorInfo.dialogStrings}
            appColors={appColors}
            actions={[closeAction]}
            debugInfo={{
              debugErrorCode: 'FileNotFoundOrAccessDenied',
              debugError: errorInfo.error
            }}
          />
        );

      case DriverErrorType.authorizationError:
        return (
          <ErrorDialog
            dialogStrings={errorInfo.dialogStrings}
            appColors={appColors}
            actions={[feedbackAction, closeAction]}
            debugInfo={{
              debugErrorCode: 'AuthorizationError',
              debugError: errorInfo.error
            }}
          />
        );

      case DriverErrorType.throttlingError:
      case ContainerErrorType.throttlingError:
        return (
          <ErrorDialog
            dialogStrings={errorInfo.dialogStrings}
            appColors={appColors}
            actions={[
              feedbackAction,
              {
                type: 'Default',
                // If service is busy we want a refresh button on load path instead of close
                label: (serviceBusyOnLoadPath ? stringTable?.reloadButtonText : stringTable?.close) || '',
                action: () => {
                  errorInfo && errorDialogActionHandlers && errorDialogActionHandlers('Close', errorInfo.errorType);
                }
              }
            ]}
            debugInfo={{
              debugErrorCode: 'Service Busy',
              debugError: errorInfo.error
            }}
          />
        );

      case OWHErrorType.ContainerUpgradeRequired:
        return (
          <ErrorDialog
            dialogStrings={errorInfo.dialogStrings}
            appColors={appColors}
            actions={[closeAction]}
            debugInfo={{
              debugErrorCode: 'ContainerUpgradeRequired',
              debugError: errorInfo.error
            }}
          />
        );

      // Filter out ODSP errors from generic error dialogs
      // TODO: Task 4663170: Show specialized dialogs for ODSP errors?
      case DriverErrorType.fileOverwrittenInStorage:
      case OdspErrorType.outOfStorageError:
        return null;

      default:
        return (
          <ErrorDialog
            dialogStrings={errorInfo.dialogStrings}
            appColors={appColors}
            actions={[feedbackAction, closeAction]}
            debugInfo={{
              debugErrorCode: errorInfo?.errorType,
              debugError: errorInfo.error
            }}
          />
        );
    }
  }

  return null;
};
